import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/layoutTemplate/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/layoutTemplate/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/layoutTemplate/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/layoutTemplate/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/layoutTemplate/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/layoutTemplate/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/layoutTemplate/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/layoutTemplate/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/layoutTemplate/UPDATE_FAILURE'
const DELETE_REQUEST = 'realhub/layoutTemplate/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/layoutTemplate/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/layoutTemplate/DELETE_FAILURE'

// Initial State
const initialState = {
  result: [],
  loaded: false,
  loadedLayoutTemplateIds: [],
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  errors: [],
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedLayoutTemplateIds: payload.loadedLayoutTemplateIds,
  }
}

export function fetchSuccess(payload = {}){
  return {
    type: FETCH_SUCCESS,
    result: payload.result,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(payload = {}){
  return {
    type: CREATE_SUCCESS,
    result: payload.result,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(payload = {}){
  return {
    type: UPDATE_SUCCESS,
    result: payload.result,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(payload = {}){
  return {
    type: DELETE_SUCCESS,
    result: payload.result,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function hydrateLayoutTemplate(layoutTemplate){
  const normalizedJson = normalize(layoutTemplate, Schemas.LAYOUT_TEMPLATE)

  return (dispatch) => {
    dispatch(updateEntities(normalizedJson))
  }
}

export function loadLayoutTemplate(layoutTemplateId, options){
  return (dispatch, getState) => {
    // Add Layout Template ID to loaded array
    const { layoutTemplates } = getState()
    const loadedLayoutTemplateIds = layoutTemplates.loadedLayoutTemplateIds
      ? layoutTemplates.loadedLayoutTemplateIds.slice()
      : []

    if (!loadedLayoutTemplateIds.includes(layoutTemplateId)){
      loadedLayoutTemplateIds.push(layoutTemplateId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedLayoutTemplateIds }))

    const promise = api(`/admin/layout_templates/${layoutTemplateId}.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function loadLayoutTemplates(options){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/admin/layout_templates.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.LAYOUT_TEMPLATE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createLayoutTemplate(layoutTemplate, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      layout_template: layoutTemplate,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api('/admin/layout_templates.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function updateLayoutTemplate(layoutTemplate, options){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      layout_template: layoutTemplate,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/layout_templates/${layoutTemplate.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function duplicateLayoutTemplate(layoutTemplate, options){
  const config = {
    method: 'POST',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/layout_templates/${layoutTemplate.id}/duplicate.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function updateLayoutTemplateProducts(layoutTemplate, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      publication_product: layoutTemplate.itemIds,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/layout_templates/${layoutTemplate.id}/update_products.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function deleteLayoutTemplate(layoutTemplate, options){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/layout_templates/${layoutTemplate.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.LAYOUT_TEMPLATE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(updateSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedLayoutTemplateIds: action.loadedLayoutTemplateIds || state.loadedLayoutTemplateIds,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        result: action.result,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return { ...state, creating: true }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        result: action.result,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return { ...state, updating: true }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return { ...state, deleting: true }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        result: action.result,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
